import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@components/atoms/Typography';
import { Space } from '@components/atoms/Space';
import { useTranslation } from 'react-i18next';
import { Container } from '@components/atoms/Container';
import styles from './EditPersonalData.module.css';
import { RoundProfilePicture } from '@components/atoms/RoundProfilePicture';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Input, InputCheckRules, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Form } from '@components/atoms/Form';
import { InputErrors } from '@types';
import { useUpdateUser } from '@services/useUpdateUser';
import { convertFilesToBase64 } from '@utils/image';
import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE_MB, REGEX_IS_EMAIL } from '@constants';
import { useNotifications } from '@hooks/useNotifications';
import { Checkbox, CheckboxType } from '@components/atoms/Checkbox';
import { LabeledInput } from '@components/atoms/Input/LabeledInput';
import { useRoleChecker } from '@hooks/useRoleChecker';

interface EditUserFormErrors {
    name: InputErrors;
    profile_photo_url: InputErrors;
    email: InputErrors;
    document_type: InputErrors;
    document_number: InputErrors;
    phone_number: InputErrors;
    address: InputErrors;
    nationality: InputErrors;
    display_name: InputErrors;
    license_number: InputErrors;
    oldPassword: string[];
    password: 'valid' | 'invalid' | null;
    passwordConfirm: string[];
}

const documentTypeOptions = [
    { value: 'CC', text: 'CC' },
    { value: 'BI', text: 'BI' },
    { value: 'PASS', text: 'PASS' },
    { value: 'CNH', text: 'CNH' },
    { value: 'AR', text: 'AR' },
    { value: 'INS', text: 'INS' },
    { value: 'DUA', text: 'DUA' },
    { value: 'OT', text: 'Other' },
];

type UserProps = {
    userData: any;
};

export const EditPersonalData: React.FC<UserProps> = ({ userData }) => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const navigate = useNavigate();
    const {
        request: requestUpdateUser,
        loading,
        error: errorUpdateUser,
        lastResponseTime,
        validationErrors,
        success,
    } = useUpdateUser();
    const { isDriver } = useRoleChecker();

    const formRef = useRef<HTMLFormElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [name, setName] = useState<string>(userData?.name ?? '');
    const [email, setEmail] = useState<string>(userData?.email ?? '');
    const [documentType, setDocumentType] = useState<string>(userData?.document_type ?? '');
    const [documentNumber, setDocumentNumber] = useState<string>(userData?.document_number ?? '');
    const [phone, setPhone] = useState<string>(userData?.phone_number ?? '');
    const [address, setAddress] = useState<string>(userData?.address ?? '');
    const [nationality, setNationality] = useState<string>(userData?.nationality ?? '');

    const [photo, setPhoto] = useState<string | null>(null);
    const [newUserPhotoPreview, setNewUserPhotoPreview] = useState<string | null>(null);
    const [photoDeleted, setPhotoDeleted] = useState(false);

    const [changePassword, setChangePassword] = useState<boolean>(false);
    const [oldPassword, setOldPassword] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setConfirmPassword] = useState<string>('');
    const [validPassword, setValidPassword] = useState<boolean>(false);

    const [displayName, setDisplayName] = useState<string>(userData?.display_name ?? '');

    const [licenseNumber, setLicenseNumber] = useState<string>(userData?.license_number ?? '');

    const translations = {
        title: t('user-area.data'),
        hint: t('user-area.hint-rider'),
        save: t('navigation.save'),
        nameLabel: t('personal-info-form.name.label'),
        emailLabel: t('personal-info-form.email.label'),
        phoneLabel: t('personal-info-form.phone.label'),
        addressLabel: t('personal-info-form.address.label'),
        documentNumberLabel: t('personal-info-form.docId.label'),
        nationalityLabel: t('personal-info-form.nationality.label'),
        nameRequiredError: t('personal-info-form.name.required-error'),
        emailRequiredError: t('personal-info-form.email.required-error'),
        emailInvalidError: t('personal-info-form.email.invalid-error'),
        docTypeLabel: t('personal-info-form.docType.label'),
        docTypeRequiredError: t('personal-info-form.docType.required-error'),
        documentNumberRequiredError: t('personal-info-form.docId.required-error'),
        phoneRequiredError: t('personal-info-form.phone.required-error'),
        photoInvalidError: t('personal-info-form.photo.invalid-image-error'),
        photoTooLargeError: t('personal-info-form.photo.too-large-error'),
        photoTitle: t('personal-info-form.photo.new'),
        photoLabelDriver: t('personal-info-form.photo.label-driver'),
        photoLabelRider: t('personal-info-form.photo.label-rider'),
        photoNew: t('personal-info-form.photo.new'),
        photoDelete: t('personal-info-form.photo.delete'),
        displayNameLabel: t('personal-info-form.display-name.label'),
        displayNameRequiredError: t('personal-info-form.display-name.required-error'),
        licenseNumberLabel: t('personal-info-form.license.number'),
        licenseNumberRequiredError: t('personal-info-form.license.number-required'),
        licenseValidLabel: t('personal-info-form.license.date'),
        licenseValidRequiredError: t('personal-info-form.license.date-required'),
        licenseExpiredError: t('personal-info-form.license.date-invalid'),
        addressRequiredError: t('personal-info-form.address.required-error'),
        nationalityRequiredError: t('personal-info-form.nationality.required-error'),
        password: {
            changePwd: t('personal-info-form.password.change-pwd.label'),
            currentPwd: t('personal-info-form.password.current-pwd.label'),
            currentPwdRequiredError: t('personal-info-form.password.current-pwd.required-error'),
            newPwd: t('personal-info-form.password.new-pwd.label'),
            newPwdRequiredError: t('personal-info-form.password.new-pwd.required-error'),
            confirmNewPwd: t('personal-info-form.password.confirm-new-pwd.label'),
            confirmNewPwdMatchingError: t('personal-info-form.password.confirm-new-pwd.match-error'),
        },
    };

    const submitForm: () => Promise<void> = async () => {
        const userPhotoInput = document.getElementById('user-photo') as HTMLInputElement;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base64userImage: any = userPhotoInput?.files ? await convertFilesToBase64(userPhotoInput.files) : null;

        const payload: Record<string, unknown> = {
            name,
            uuid: userData?.uuid,
            email: email,
            phone_number: phone,
            address,
            nationality,
            document_type: documentType,
            document_number: documentNumber,
            user_type: userData?.user_type,
        };

        if (changePassword) {
            payload.old_password = oldPassword;
            payload.password = password;
            payload.password_confirmation = passwordConfirm;
        }

        if (isDriver) {
            payload.display_name = displayName;
            payload.license_number = licenseNumber;
        }

        if (photoDeleted) {
            payload.profile_photo_url = null;
        } else if (photo) {
            payload.profile_photo_url = base64userImage?.[0];
        }

        requestUpdateUser(payload);
    };

    const [errors, setErrors] = useState<EditUserFormErrors>({
        name: [],
        profile_photo_url: [],
        email: [],
        document_type: [],
        document_number: [],
        phone_number: [],
        address: [],
        nationality: [],
        license_number: [],
        display_name: [],
        password: null,
        oldPassword: [],
        passwordConfirm: [],
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handlePhotoClick = () => {
        setPickerOpen(true); // open picker
    };

    const handlePicture = (choice: 'new' | 'delete') => {
        switch (choice) {
            case 'new':
                fileInputRef.current?.click();
                break;

            case 'delete':
                setPhoto(null);
                setNewUserPhotoPreview(null);
                setPhotoDeleted(true);
                if (fileInputRef.current) fileInputRef.current.value = '';
                break;
        }

        setPickerOpen(false);
    };

    const handleFileChange = async (files: FileList | null) => {
        const fileInput = fileInputRef.current;
        if (files && files.length > 0) {
            const file = files[0];

            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                setErrors((prev) => ({
                    ...prev,
                    profile_photo_url: [translations.photoInvalidError],
                }));
                if (fileInput) fileInput.value = '';
                return;
            }

            const fileSizeMB = file.size / 1024 / 1024;
            if (fileSizeMB > MAX_IMAGE_SIZE_MB) {
                setErrors((prev) => ({
                    ...prev,
                    profile_photo_url: [`${translations.photoTooLargeError} ${MAX_IMAGE_SIZE_MB}MB.`],
                }));
                if (fileInput) fileInput.value = '';
                return;
            }

            const base64 = await convertFilesToBase64(files);
            setPhoto(base64[0] as string);
            setPhotoDeleted(false);

            setErrors((prev) => ({
                ...prev,
                profile_photo_url: [],
            }));
        }
    };

    const handleOnClickUpdateUser = () => {
        const errors = validateForm();

        const isPasswordValid = !changePassword || errors.password === 'valid';
        const isOldPasswordValid = !changePassword ? true : oldPassword.trim().length > 0;

        if (
            errors.name.length === 0 &&
            errors.email.length === 0 &&
            errors.address.length === 0 &&
            errors.nationality.length === 0 &&
            errors.document_type.length === 0 &&
            errors.document_number.length === 0 &&
            errors.phone_number.length === 0 &&
            errors.profile_photo_url.length === 0 &&
            isPasswordValid &&
            isOldPasswordValid &&
            errors.passwordConfirm.length === 0 &&
            (!isDriver || (errors.license_number.length === 0 && errors.display_name.length === 0))
        ) {
            formRef?.current?.requestSubmit();
        }
    };

    const validateForm: () => EditUserFormErrors = () => {
        const newErrors: EditUserFormErrors = {
            name: [],
            profile_photo_url: [],
            email: [],
            document_type: [],
            document_number: [],
            phone_number: [],
            address: [],
            display_name: [],
            license_number: [],
            nationality: [],
            oldPassword: [],
            password: changePassword ? (validPassword ? 'valid' : 'invalid') : null,
            passwordConfirm: [],
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !name.trim() && newErrors.name.push(translations.nameRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !phone.trim() && newErrors.phone_number.push(translations.phoneRequiredError);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        !address.trim() && newErrors.address.push(translations.addressRequiredError);

        if (!email.trim()) {
            newErrors.email.push(translations.emailRequiredError);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            !REGEX_IS_EMAIL.test(email.trim()) && newErrors.email.push(translations.emailInvalidError);
        }

        if (photo != null && !photo.startsWith('data:image/')) {
            newErrors.profile_photo_url.push(translations.photoInvalidError);
        }

        if (isDriver) {
            if (!licenseNumber.trim()) {
                newErrors.license_number.push(translations.licenseNumberRequiredError);
            }

            if (!displayName.trim()) {
                newErrors.display_name.push(translations.displayNameRequiredError);
            }
        }

        if (changePassword) {
            if (!oldPassword.trim()) {
                newErrors.oldPassword.push(translations.password.currentPwdRequiredError);
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            newErrors.passwordConfirm.length === 0 &&
                password !== passwordConfirm &&
                newErrors.passwordConfirm.push(translations.password.confirmNewPwdMatchingError);
        }

        setErrors(newErrors);

        return newErrors;
    };

    useEffect(() => {
        if (!pickerOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
                setPickerOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [pickerOpen]);

    useEffect(() => {
        if (!lastResponseTime) return;

        if (success) {
            showDefaultNotification({ type: 'success', message: success ?? '' });
            navigate('/my-profile');
            return;
        }

        if (validationErrors) {
            setErrors((prev) => ({
                ...prev,
                name: validationErrors.name || [],
                profile_photo_url: validationErrors.profile_photo_url || [],
                email: validationErrors.email || [],
                document_type: validationErrors.document_type || [],
                document_number: validationErrors.document_number || [],
                phone_number: validationErrors.phone_number || [],
                nationality: validationErrors.nationality || [],
                address: validationErrors.address || [],
            }));
            return;
        }

        showDefaultNotification({ type: 'error', message: errorUpdateUser ?? '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    useEffect(() => {
        const logoInput = document.getElementById('user-photo') as HTMLInputElement;
        const file = logoInput?.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = () => setNewUserPhotoPreview(reader.result as string);
            reader.readAsDataURL(file);
        } else {
            setNewUserPhotoPreview(null);
        }
    }, [photo]);

    return (
        <Container classes={styles.containerBackground}>
            <div className="d-flex align-items-center position-relative">
                <div className="position-absolute start-0">
                    <RoundIcon
                        classes={styles.roundIcon}
                        icon="fa-thin fa-arrow-left"
                        variant={RoundIconVariants.variant1}
                        onClick={() => navigate('/my-profile')}
                    />
                </div>

                <Typography fontSize="24" fontWeight="800" classes="m-auto">
                    {translations.title}
                </Typography>
            </div>

            <Space spaceBreakpoints={{ xs: { space: 30 } }} />

            <Form formRef={formRef} onSubmit={() => submitForm()}>
                <div onClick={handlePhotoClick} style={{ cursor: 'pointer' }}>
                    <RoundProfilePicture
                        src={photoDeleted ? undefined : (newUserPhotoPreview ?? userData?.profile_photo_url ?? undefined)}
                        icon="fa-thin fa-user-tie-hair"
                        hintText={translations.hint}
                        variant="edit"
                    />
                    <Typography color="danger" align="center">
                        <span>{errors.profile_photo_url}</span>
                    </Typography>
                </div>

                <>
                    {pickerOpen && (
                        <div className={styles.overlay}>
                            <div className={styles.pickerContainer} ref={pickerRef}>
                                <RoundProfilePicture
                                    icon="fa-thin fa-camera"
                                    labelText={translations.photoTitle}
                                    hintText={isDriver ? translations.photoLabelDriver : translations.photoLabelRider}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 30 } }} />
                                <Button
                                    label={translations.photoNew}
                                    mode={ButtonMode.primary}
                                    classes={styles.button}
                                    onClick={() => handlePicture('new')}
                                />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                <Button
                                    label={translations.photoDelete}
                                    mode={ButtonMode.primary}
                                    classes={styles.button}
                                    outline
                                    onClick={() => handlePicture('delete')}
                                />
                            </div>
                        </div>
                    )}
                </>

                <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                <hr />

                <Space spaceBreakpoints={{ xs: { space: 30 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.nameLabel}*`}
                    value={name}
                    onChange={(value: string) => setName(value)}
                    state={errors.name.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.name}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                {isDriver ? (
                    <>
                        <Input
                            variant={InputVariants.labeledInput}
                            type={InputTypes.text}
                            label={`${translations.displayNameLabel}*`}
                            value={displayName}
                            onChange={(value: string) => setDisplayName(value)}
                            state={errors.display_name.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.display_name}
                        />

                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                ) : (
                    <></>
                )}

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.email}
                    label={`${translations.emailLabel}`}
                    value={email}
                    onChange={(value: string) => setEmail(value)}
                    state={errors.email.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.email}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                {/* change password */}
                <Checkbox
                    type={CheckboxType.normal}
                    name="change-password"
                    value="true"
                    checked={changePassword}
                    onChange={() => setChangePassword((prev) => !prev)}
                    label={
                        <Typography fontSize="12">
                            <>{`${translations.password.changePwd} `}</>
                        </Typography>
                    }
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                {/* oldPassword */}
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.password.currentPwd}*`}
                    value={oldPassword}
                    onChange={(value) => {
                        setOldPassword(value);
                    }}
                    state={errors.oldPassword.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.oldPassword}
                    disabled={!changePassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                {/* password */}
                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.password.newPwd}*`}
                    value={password}
                    onChange={(value) => setPassword(value)}
                    state={errors.password === 'invalid' ? InputStates.error : undefined}
                    checkRules={InputCheckRules.passwordBuild}
                    onValidationChange={setValidPassword}
                    disabled={!changePassword}
                />
                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                {/* confPassword */}

                <Input
                    type={InputTypes.password}
                    variant={InputVariants.labeledInput}
                    label={`${translations.password.confirmNewPwd}*`}
                    value={passwordConfirm}
                    onChange={(value) => setConfirmPassword(value)}
                    state={errors.passwordConfirm.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.passwordConfirm}
                    disabled={!changePassword}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <LabeledInput
                    type={InputTypes.select}
                    label={`${translations.docTypeLabel}`}
                    options={documentTypeOptions}
                    value={documentType}
                    onChange={(value) => setDocumentType(value)}
                    state={errors.document_type.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.document_type}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.documentNumberLabel}`}
                    value={documentNumber}
                    onChange={(value) => setDocumentNumber(value)}
                    state={errors.document_number.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.document_number}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.tel}
                    label={`${translations.phoneLabel}*`}
                    value={phone}
                    onChange={(value: string) => setPhone(value)}
                    state={errors.phone_number.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.phone_number}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                {isDriver ? (
                    <>
                        <Input
                            variant={InputVariants.labeledInput}
                            type={InputTypes.text}
                            label={`${translations.licenseNumberLabel}*`}
                            value={licenseNumber}
                            onChange={(value: string) => setLicenseNumber(value)}
                            state={errors.license_number.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.license_number}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </>
                ) : (
                    <></>
                )}

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.text}
                    label={`${translations.nationalityLabel}`}
                    value={nationality}
                    onChange={(value: string) => setNationality(value)}
                    state={errors.nationality.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.nationality}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    variant={InputVariants.labeledInput}
                    type={InputTypes.textarea}
                    label={`${translations.addressLabel}*`}
                    value={address}
                    onChange={(value: string) => setAddress(value)}
                    state={errors.address.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.address}
                />

                <Space spaceBreakpoints={{ xs: { space: 15 } }} />

                <Input
                    id="user-photo"
                    ref={fileInputRef}
                    classes="d-none"
                    type={InputTypes.file}
                    label={t('my-profile-component.my-account.photo.label')}
                    variant={InputVariants.labeledInput}
                    value={photo || ''}
                    onChange={handleFileChange}
                    state={errors.profile_photo_url.length > 0 ? InputStates.error : undefined}
                    errorMessages={errors.profile_photo_url}
                />

                <Button
                    label={translations.save}
                    mode={ButtonMode.primary}
                    onClick={() => handleOnClickUpdateUser()}
                    disabled={loading}
                    classes="w-100"
                />
            </Form>
        </Container>
    );
};
